import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CalendarEvent } from 'angular-calendar';
import { CalendarService } from 'app/services/calendar.service';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { SchoolActivity, SchoolActivityInstance } from '../models';
import { FeatureFlagService, FLAGS } from '../services/feature-flag.service';
import { FlexPeriod, FlexPeriodService } from '../services/flex-period.service';
import { SchoolActivityInstancesForPeriodReq, SchoolActivityService, SignUpForActivityReq } from '../services/school-activity.service';
import { UserService } from '../services/user.service';

enum ProcessingState {
	Loading,
	Loaded,
	Error,
}

export type VisibleInstances = { instance: SchoolActivityInstance; activity: SchoolActivity };
export type VisibleInstancesWithLoading = {
	instances: VisibleInstances[];
	loading: boolean;
	error: boolean;
};

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'sp-activity-sign-up',
	templateUrl: './activity-sign-up.component.html',
	styleUrls: ['./activity-sign-up.component.scss'],
})
export class ActivitySignUpComponent implements OnInit, OnDestroy {
	flexOptions: { name: string; value: number }[] = [];
	private visibleInstances: VisibleInstances[] = [];
	visibleInstances$: Observable<VisibleInstancesWithLoading> | null = null;
	private searchValue$: BehaviorSubject<string> = new BehaviorSubject<string>('');
	private filterForAvailable$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
	filters: UntypedFormGroup | null = null;
	private processing = ProcessingState.Loading;

	private userId: number | null = null;
	private destroy$: Subject<void> = new Subject<void>();
	private activities: SchoolActivity[] = [];
	private activityMap = new Map<number, SchoolActivity>();
	private instances: SchoolActivityInstance[] = [];
	private flexPeriod: FlexPeriod | null = null;

	constructor(
		public flexPeriodService: FlexPeriodService,
		public activityService: SchoolActivityService,
		public featureFlagService: FeatureFlagService,
		private userService: UserService,
		public calendarService: CalendarService,
		public dialogRef: MatDialogRef<ActivitySignUpComponent>,
		@Inject(MAT_DIALOG_DATA)
		public data: {
			flexPeriodId?: number;
			date?: Date;
			selectedActivityId: number;
			calendarEvent?: CalendarEvent;
		}
	) {}

	ngOnInit(): void {
		// Fetch updated activities for this sign up period
		if (this.data.flexPeriodId) {
			this.activityService.GetActivities(this.data.flexPeriodId);
		}

		this.setProcessingState(ProcessingState.Loading);
		this.activityService.instancesLoaded$.next(false);
		const initFP = 0;
		if (this.data.date) {
			this.filters = new UntypedFormGroup({
				flexPeriod: new UntypedFormControl({ value: initFP, disabled: true }),
				search: new UntypedFormControl(''),
				date: new UntypedFormControl({ value: this.data?.date.getDay(), disabled: true }),
				availability: new UntypedFormControl(true),
			});
			this.filters
				.get('availability')
				?.valueChanges.pipe(takeUntil(this.destroy$))
				.subscribe((value) => {
					this.filterForAvailable$.next(value === 'true');
				});

			this.filters
				.get('search')
				?.valueChanges.pipe(takeUntil(this.destroy$))
				.subscribe((searchValue) => {
					this.searchValue$.next(searchValue);
				});
		}

		if (this.data.flexPeriodId) {
			this.visibleInstances$ = combineLatest([this.activityService.activities$, this.flexPeriodService.flexPeriod$(this.data.flexPeriodId)]).pipe(
				switchMap(([activities, period]) => {
					const filtered = this.filterActivities(
						Object.values(activities.entities)
							.filter((a): a is SchoolActivity => !!a)
							.filter((a) => a.flex_period_id == this.data.flexPeriodId)
					);
					return combineLatest([of(filtered), of(period)]);
				}),
				filter(([_activities, period]) => !!period),
				switchMap(([activities, period]) => {
					this.activities = activities;
					this.activityMap = new Map(activities.map((activity) => [activity.id, activity]));

					if (period) {
						this.flexPeriod = period;
						this.flexOptions = [{ value: this.flexPeriod.id, name: this.flexPeriod.name }];
						this.filters?.patchValue({ flexPeriod: this.flexPeriod.id });
					}
					if (this.data?.date && activities?.length > 0 && this.flexPeriod) {
						const req: SchoolActivityInstancesForPeriodReq = {
							day: this.data.date,
							activities: this.activities,
							flexPeriod: this.flexPeriod,
							timezone: this.userService.getUserSchool().timezone,
						};
						this.activityService.GetActivityInstancesByPeriod(req);
					}
					return combineLatest([
						of(activities),
						this.activityService.instancesForDate$(this.data.date!),
						this.activityService.instancesLoaded$,
						this.searchValue$,
						this.filterForAvailable$,
					]);
				}),
				map(([activities, instances, loaded, search, filterForAvailable]) => {
					this.visibleInstances = [];
					if (activities.length === 0) {
						const result: VisibleInstancesWithLoading = {
							loading: false,
							instances: this.visibleInstances,
							error: false,
						};
						return result;
					}
					this.instances = instances.filter((i): i is SchoolActivityInstance => !!i);
					for (const instance of instances) {
						const activity = this.activityMap.get(instance!.activity_id) || null;
						const instanceCopy: SchoolActivityInstance = {
							...instance!,
							selected: this.data.selectedActivityId ? this.data.selectedActivityId === activity?.id : false,
						};
						if (activity) {
							this.visibleInstances.push({ instance: instanceCopy, activity });
						}
					}
					if (filterForAvailable) {
						this.visibleInstances = this.filterAvailableInstances(this.visibleInstances);
					}
					if (loaded) {
						this.setProcessingState(ProcessingState.Loaded);
					}
					if (search) {
						this.visibleInstances = this.filterInstancesBySearch(this.visibleInstances, search);
					}
					const result: VisibleInstancesWithLoading = {
						loading: !loaded,
						instances: this.visibleInstances,
						error: false,
					};
					return result;
				}),
				catchError(() => {
					return of({
						loading: false,
						instances: [],
						error: true,
					});
				})
			);
		}

		this.userService.userJSON$
			.pipe(
				takeUntil(this.destroy$),
				switchMap((user) => {
					this.userId = user.id;
					return this.activityService.WatchActivityEventsForStudent();
				})
			)
			.subscribe();
	}

	private filterActivities(activities: SchoolActivity[]): SchoolActivity[] {
		if (!this.featureFlagService.isFeatureEnabledV2(FLAGS.ActivityManagement)) {
			return activities.filter((a): a is SchoolActivity => {
				return a.public_event || a.id === this.data.selectedActivityId;
			});
		}
		return activities;
	}

	signUp(instance: SchoolActivityInstance): void {
		if (this.processing !== ProcessingState.Loaded) {
			return;
		}
		this.setProcessingState(ProcessingState.Loading);

		const selectedInstance = this.visibleInstances.find((i) => i.instance.selected);
		if (this.data.selectedActivityId && selectedInstance && !instance.selected) {
			return this.switchActivity(selectedInstance.instance, instance);
		}

		this.visibleInstances = [];
		this.signUpForActivity(instance);
	}

	// If a student is switching activities--remove them from currently selected
	// before signing up for activity
	private switchActivity(oldInstance: SchoolActivityInstance, newInstance: SchoolActivityInstance): void {
		oldInstance.selected = false;
		this.visibleInstances = [];
		this.activityService
			.GetAttendeeRecordForStudent(oldInstance.id, this.userId!, new Date(oldInstance.start_time))
			.pipe(takeUntil(this.destroy$))
			.subscribe((attendeeRecord) => {
				if (attendeeRecord) {
					const req: SignUpForActivityReq = this.getSignUpForActivityReq(newInstance);
					this.activityService.RemoveAttendeeFromActivityInstanceAndSignUp(attendeeRecord.id, req, newInstance);
					this.dialogRef.close(newInstance);
					return;
				}
				this.signUpForActivity(newInstance);
			});
	}

	private getSignUpForActivityReq(instance: SchoolActivityInstance): SignUpForActivityReq {
		const req: SignUpForActivityReq = {
			user_id: this.userId!,
			activity_id: instance.activity_id,
			scheduled_date: new Date(instance.start_time),
			activity_instance_id: instance.id,
		};
		return req;
	}

	private filterAvailableInstances(instances: VisibleInstances[]): VisibleInstances[] {
		return instances
			.filter((ai) => {
				const currentAttendees = ai.instance.current_num_attendees ?? 0;
				return ai.activity && (currentAttendees < ai.activity.max_attendees || ai.activity.max_attendees === 0 || ai.instance.selected);
			})
			.filter((i) => !!i);
	}

	private filterInstancesBySearch(instances: VisibleInstances[], searchQuery: string): VisibleInstances[] {
		return instances
			.filter((ai) => {
				return (
					ai.activity &&
					(ai.activity.name.toLowerCase().includes(searchQuery) ||
						ai.activity.teacher_name?.toLowerCase().includes(searchQuery) ||
						ai.instance.selected)
				);
			})
			.filter((i) => !!i);
	}

	private signUpForActivity(instance: SchoolActivityInstance): void {
		const req: SignUpForActivityReq = this.getSignUpForActivityReq(instance);
		this.activityService.StudentSignUpForActivity(req, instance);
		this.setProcessingState(ProcessingState.Loaded);
		this.dialogRef.close(instance);
	}

	private setProcessingState(state: ProcessingState): void {
		this.processing = state;
	}

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}
}
